import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';

/**
 * iter 144 - Partner-message notification bell.
 *
 * Separate from the generic NotificationBell because partner messages
 * are a distinct surface (managed marketplace conversations) and the
 * spec calls for a dedicated icon with an amber unread badge that
 * deep-links to /portal/messages.
 *
 * Polls /api/partner-messages/unread-count every 30s. Defaults silently
 * to 0 when the endpoint is unavailable so we never surface a console
 * error to the member.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 30_000;

const PartnerMessageBell = ({ variant = 'header' }) => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  const fetchCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/partner-messages/unread-count`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) return;
      const d = await r.json();
      setUnread(d.unread || 0);
    } catch (_) { /* noop - silent for the bell */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, [fetchCount]);

  const sizeCls = variant === 'sidebar' ? 'w-9 h-9' : 'w-9 h-9';

  return (
    <button
      type="button"
      onClick={() => navigate('/portal/messages')}
      className={`relative ${sizeCls} flex items-center justify-center rounded-full text-[#0A1F2E] hover:bg-[#F4ECD8] transition`}
      aria-label={
        unread > 0
          ? `${unread} unread partner message${unread === 1 ? '' : 's'}`
          : 'Partner messages'
      }
      data-testid="partner-message-bell"
      title="Partner messages"
    >
      <Mail className="w-5 h-5" />
      {unread > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E8A830] text-[#0A1F2E] text-[10px] font-bold flex items-center justify-center font-mono"
          data-testid="partner-message-bell-count"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </button>
  );
};

export default PartnerMessageBell;
